import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { updatePlatform } from 'src/models/platform/platform';
import { defaultStats } from 'src/models/user/utils';
import { FormAddableSelect } from 'src/shared/Forms/Selects/AddableSelect';
import toast from 'src/utils/toast';

interface AddMeasurementDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AddMeasurementDialog = ({
  open,
  onClose,
}: AddMeasurementDialogProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessmentQuestionField',
  });
  const { t: tGlobal } = useTranslation();
  const { session } = useSession();
  const queryClient = useQueryClient();

  // Get default metrics labels - matches Ember's defaultMetrics computed property
  const defaultMetrics = defaultStats
    .filter((stat) => stat.stat !== 'height')
    .map((stat) => stat.label);

  // Build statOptions: default metrics + platform's healthInfoMetrics
  const statOptions = (() => {
    const options = [...defaultMetrics];
    if (session?.healthInfoMetrics) {
      options.push(...(session.healthInfoMetrics as string[]));
    }
    // Remove duplicates
    return [...new Set(options)].map((label) => ({ id: label, title: label }));
  })();

  // Get initial selected tags: default metrics + healthInfoMetrics - defaultHealthMetrics
  const getInitialSelectedTags = (): string[] => {
    if (!session) return [];

    let stats = [...defaultMetrics];
    if (session.healthInfoMetrics) {
      stats = stats.concat(session.healthInfoMetrics as string[]);
    }
    if (
      session.defaultHealthMetrics &&
      session.defaultHealthMetrics.length > 0
    ) {
      stats = stats.filter(
        (r) => !session.defaultHealthMetrics?.includes(r)
      );
    }
    return stats;
  };

  // Calculate defaultHealthMetrics and healthInfoMetrics from selected fields
  const calculateHealthFields = (fields: string[]) => {
    // defaultFields = default metrics that are NOT selected (these go to defaultHealthMetrics)
    const defaultFields = defaultMetrics.filter((f) => !fields.includes(f));

    // custom = selected fields that are NOT default metrics (these are custom metrics)
    const custom = fields.filter((f) => !defaultMetrics.includes(f));

    return {
      defaultHealthMetrics: defaultFields,
      healthInfoMetrics: custom,
    };
  };

  const updatePlatformMutation = useMutation({
    mutationFn: (data: {
      defaultHealthMetrics: string[];
      healthInfoMetrics: string[];
    }) => {
      if (!session) {
        throw new Error('Session not available');
      }
      return updatePlatform(session.id, data);
    },
    onSuccess: () => {
      toast.success(
        `${tGlobal('trainer.client.one')} ${tGlobal('measurement.plural')} ${t(
          'saved'
        )}!`
      );
      queryClient.invalidateQueries({
        queryKey: ['user', session?.trainerId],
      });
      queryClient.invalidateQueries({
        queryKey: ['session'],
      });
      onClose();
    },
    onError: () => {
      toast.error(t('errorUpdatingMeasurements'));
    },
  });

  interface FormValues {
    selectedTags: string[];
  }

  const handleSubmit = (values: FormValues) => {
    if (!session) return;

    const { defaultHealthMetrics, healthInfoMetrics } =
      calculateHealthFields(values.selectedTags);

    // Check if values changed
    const currentDefaultMetrics = session.defaultHealthMetrics?.toString() || '';
    const newDefaultMetrics = defaultHealthMetrics.toString();
    const currentHealthInfoMetrics = (session.healthInfoMetrics as string[])?.toString() || '';
    const newHealthInfoMetricsStr = healthInfoMetrics.toString();

    if (
      currentDefaultMetrics !== newDefaultMetrics ||
      currentHealthInfoMetrics !== newHealthInfoMetricsStr
    ) {
      updatePlatformMutation.mutate({
        defaultHealthMetrics,
        healthInfoMetrics,
      });
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!session) {
    return null;
  }

  const initialValues: FormValues = {
    selectedTags: getInitialSelectedTags(),
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ submitForm, setFieldValue }) => (
        <Dialog
          open={open}
          onClose={handleClose}
          title={`${t('add')}/${t('remove')} ${tGlobal('measurement.plural')}`}
          maxWidth="sm"
          fullWidth
          actions={[
            {
              label: t('save'),
              onClick: submitForm,
              variant: 'contained',
              loading: updatePlatformMutation.isPending,
            },
          ]}
        >
          <Form>
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('addOrRemoveCustom')} {tGlobal('trainer.client.one')}{' '}
                {tGlobal('measurement.plural')}
              </Typography>
              <FormAddableSelect
                name="selectedTags"
                label=""
                options={statOptions}
                placeholder={`${t('add')} ${tGlobal('measurement.one')}`}
                keepUnmatched
                onChange={(fields) => {
                  // Update health fields when tags change (like Ember's tag-autocomplete with action)
                  // This is called immediately but doesn't save yet
                  const { defaultHealthMetrics, healthInfoMetrics } =
                    calculateHealthFields(fields);
                  // Store in formik values for later use in submit
                  setFieldValue('defaultHealthMetrics', defaultHealthMetrics);
                  setFieldValue('healthInfoMetrics', healthInfoMetrics);
                }}
              />
            </Box>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};
